/* eslint-disable no-mixed-spaces-and-tabs */
import React from 'react';
import { useGetRecentList } from '@/hooks/useGetRecentList';
import {
	Chip,
	IconButton,
	ListItemIcon,
	ListSubheader,
	Menu,
	MenuItem,
	Stack,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { NavLink, useLocation } from 'react-router-dom';
import { Person } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { removeRecentlyViewed } from '@/features/userPrefs/userPrefsSlice';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { getObjectChipProps } from '@/utils/objectChip';

const Item = ({ client, onClose }) => {
	const theme = useTheme();
	const dispatch = useDispatch();
	const location = useLocation();

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const menuItemProps = {
		sx: {
			width: '100%',
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'center',
			px: 2,
			'&:hover': { backgroundColor: theme.palette.action.hover },
			'& .MuiListItemIcon-root': { minWidth: 32 },
		},
	};
	const chipProps = getObjectChipProps('prospect', { theme, isProspect: client?.isProspect });
	const disabled = location.pathname.includes(client.path);
	return (
		<Tooltip
			key={`client-${client.id}-tooltip`}
			placement='left'
			title={disabled ? 'You are already viewing this client' : ''}
		>
			<MenuItem
				key={`client-${client.id}`}
				{...menuItemProps}
				{...(!disabled && {
					component: NavLink,
					to: client.path,
				})}
			>
				<Stack
					direction='row'
					alignItems='center'
					spacing={1}
					{...(disabled && {
						sx: { opacity: 0.5, pointerEvents: 'none' },
					})}
				>
					<ListItemIcon>
						<Person fontSize='small' />
					</ListItemIcon>
					<Typography variant='inherit' noWrap>
						{client.name}
					</Typography>
					{client.isProspect && (
						<Chip
							size='small'
							label='PROSPECT'
							variant='outlined'
							color='primary'
							sx={{
								...chipProps,
								fontSize: '12px',
							}}
						/>
					)}
				</Stack>

				<IconButton
					size='small'
					edge='end'
					{...(disabled && { sx: { opacity: 'unset !important' } })}
					onClick={(e) => {
						e.stopPropagation();
						e.preventDefault();
						dispatch(
							removeRecentlyViewed({
								userId: userDetails.userId,
								id: client.id,
								type: 'clients',
							}),
						);
						onClose();
					}}
				>
					<ClearIcon
						sx={{
							fontSize: '1.25rem !important',
							...(disabled && { opacity: 'unset !important' }),
						}}
					/>
				</IconButton>
			</MenuItem>
		</Tooltip>
	);
};

const RecentClientsMenu = ({ anchorEl, open, onClose }) => {
	const theme = useTheme();
	const { clients } = useGetRecentList();

	return (
		<Menu
			id='recent-clients-menu-appbar'
			anchorEl={anchorEl}
			anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
			keepMounted
			transformOrigin={{ vertical: 'top', horizontal: 'right' }}
			open={open}
			onClose={onClose}
			sx={{
				'& .MuiListItemText-primary': {
					fontSize: '13px',
					color: theme.palette.text.primary,
				},
				'& .MuiListItemIcon-root': {
					minWidth: '2.25em',
				},
			}}
		>
			<ListSubheader sx={{ fontSize: '14px', lineHeight: '32px' }}>
				{'Recently viewed clients'}
			</ListSubheader>
			{clients.length > 0 ? (
				clients.map((client, i) => (
					<Item key={`client-menu-${i}`} client={client} onClose={onClose} />
				))
			) : (
				<Typography px={'16px'} variant='task_updated'>
					{'No recently viewed clients.'}
				</Typography>
			)}
		</Menu>
	);
};

export default RecentClientsMenu;
